<template>
  <div class="gallery-selector">
    <n-select
      v-model:value="selectedGalleryId"
      filterable
      clearable
      placeholder="Select a gallery..."
      :options="galleryOptions"
      :loading="loading"
      @update:value="handleSelectionChange"
      label-field="label"
      value-field="value"
      class="gallery-select"
    >
      <template #header>
        <div class="gallery-selector-header">
          <button @click="showManager = true" class="manage-galleries-button">
            <svg viewBox="0 0 24 24" width="16" height="16">
              <path fill="currentColor" d="M19,13H13V19H11V13H5V11H11V5H13V11H19V13Z" />
            </svg>
            Manage Galleries
          </button>
        </div>
      </template>
    </n-select>

    <!-- Gallery Manager Modal -->
    <n-modal v-model:show="showManager" preset="card" title="Manage Galleries" style="width: 800px">
      <GalleryManager :brand-id="brandId" @gallery-updated="loadGalleries" />
    </n-modal>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, watch } from 'vue'
import { NSelect, NModal, useMessage } from 'naive-ui'
import { apiClient } from '../services/api'
import GalleryManager from './GalleryManager.vue'

const message = useMessage()

interface Gallery {
  id: string
  name: string
  description?: string
  ownerBrandId?: string
  isPrivate: boolean
  isDefault: boolean
  galleryType?: 'images' | 'type-library'
  permissions: any[]
  accessLevel: 'READ' | 'WRITE' | 'ADMIN'
  imageCount?: number
}

const props = defineProps<{
  modelValue?: string | null
  brandId?: string
}>()

const emit = defineEmits<{
  (e: 'update:modelValue', value: string | null): void
  (e: 'change', gallery: Gallery | null): void
}>()

const loading = ref(false)
const galleries = ref<Gallery[]>([])
const selectedGalleryId = ref<string | null>(props.modelValue ?? null)
const showManager = ref(false)

const galleryOptions = computed(() => {
  return galleries.value.map(g => ({
    label: g.name + (g.isDefault ? ' (Default)' : ''),
    value: g.id,
    disabled: false,
  }))
})

const selectedGallery = computed(() => {
  if (!selectedGalleryId.value) return null
  return galleries.value.find(g => g.id === selectedGalleryId.value) ?? null
})

const loadGalleries = async () => {
  loading.value = true
  try {
    const response = await apiClient.fetchGalleries(props.brandId)
    galleries.value = response.galleries
    // When no gallery is selected, default to the default gallery (or first)
    if (!selectedGalleryId.value && galleries.value.length > 0) {
      const defaultGallery = galleries.value.find((g) => g.isDefault) ?? galleries.value[0]
      selectedGalleryId.value = defaultGallery.id
      emit('update:modelValue', defaultGallery.id)
      emit('change', defaultGallery)
    }
  } catch (error: any) {
    message.error(`Failed to load galleries: ${error.message || 'Unknown error'}`)
  } finally {
    loading.value = false
  }
}

const handleSelectionChange = (id: string | null) => {
  selectedGalleryId.value = id
  emit('update:modelValue', id)
  emit('change', selectedGallery.value)
}

watch(() => props.modelValue, (newVal) => {
  selectedGalleryId.value = newVal ?? null
})

watch(() => props.brandId, () => {
  selectedGalleryId.value = null
  loadGalleries()
})

onMounted(() => {
  loadGalleries()
})
</script>

<style scoped>
.gallery-selector {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.gallery-select {
  width: 100%;
}

.gallery-selector-header {
  padding: 8px;
  border-bottom: 1px solid var(--border-color);
}

.manage-galleries-button {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  background: var(--primary-color);
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 14px;
  transition: background 0.2s;
}

.manage-galleries-button:hover {
  background: var(--primary-color-hover);
}

</style>
